import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { engineeringExcellenceExl001Api, type Exl001Status, type SprintDashboard, type SprintValidation } from '../../api/engineering-excellence-exl001';

type Tab = 'overview' | 'modules' | 'categories' | 'providers' | 'jobs' | 'gates' | 'status';

export function EngineeringExcellenceExl001Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [categories, setCategories] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Exl001Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-amber-200 bg-white dark:border-amber-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-amber-700 dark:text-amber-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, cats, prov, jb, gt, st] = await Promise.all([
        engineeringExcellenceExl001Api.dashboard(), engineeringExcellenceExl001Api.excellenceModules(),
        engineeringExcellenceExl001Api.standardCategories(), engineeringExcellenceExl001Api.providers(),
        engineeringExcellenceExl001Api.backgroundJobs(), engineeringExcellenceExl001Api.qualityGates(),
        engineeringExcellenceExl001Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setCategories(cats); setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await engineeringExcellenceExl001Api.validate({
        critical_bugs_clear: true, security_within_policy: true,
        performance_budgets_met: true, architecture_compliant: true,
        coverage_thresholds_met: true, static_analysis_passed: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Engineering Excellence passes all release gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Excellence Modules' },
    { id: 'categories', label: 'Standard Categories' }, { id: 'providers', label: 'Providers' },
    { id: 'jobs', label: 'Jobs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-amber-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>EXL-000001 · P0 · Mandatory Engineering Standard</p>
        <h1 className="text-2xl font-semibold">Engineering Excellence & World-Class Standards</h1>
        <p className={`text-sm ${muted}`}>Enterprise-grade quality, reliability, maintainability and scalability — engineering quality as competitive advantage.</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/ai-governance-gov001" className={`${accent} hover:underline`}>GOV-000001</Link>
          <span className={muted}>→</span>
          <Link to="/engineering-excellence" className={`${accent} hover:underline`}>Live Excellence Command Center</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-amber-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Maintainable and Scalable for the Next Decade</h2>
            <p className={`mt-2 text-sm ${muted}`}>Code quality, architecture compliance, technical debt governance, reliability engineering, performance budgets, observability, testing and release engineering — all measurable and enforceable.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            {[{ label: 'Objectives', value: dashboard.objectives }, { label: 'Excellence Modules', value: dashboard.excellence_modules },
              { label: 'Standard Categories', value: dashboard.standard_categories }, { label: 'Quality Gates', value: dashboard.quality_gates }].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>{m.label}</p><p className="text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-amber-600 px-4 py-2 text-sm text-white disabled:opacity-50">Validate Release Gates</button>
          {validation && <p className={`text-sm ${validation.valid ? 'text-amber-600' : 'text-red-600'}`}>{validation.valid ? 'All gates passed' : validation.gaps?.join(', ')}</p>}
        </div>
      )}

      {tab === 'modules' && modules.map((m) => (
        <div key={m.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{m.name}</p><p className={`text-xs ${muted}`}>{m.category}</p></div>
      ))}

      {tab === 'categories' && categories.map((c) => (
        <div key={c.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{c.name}</p><p className={`text-xs ${muted}`}>{c.category}</p></div>
      ))}

      {tab === 'providers' && providers.map((p) => (
        <div key={p.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{p.name}</p><p className={`text-xs ${muted}`}>{p.interface}</p></div>
      ))}

      {tab === 'jobs' && jobs.map((j) => (
        <div key={j.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{j.name}</p><p className={`text-xs ${muted}`}>{j.schedule ?? 'on-demand'}</p></div>
      ))}

      {tab === 'gates' && gates.map((g) => (
        <div key={g.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{g.name}</p><p className={`text-xs ${muted}`}>{g.blocking ? 'Blocking' : 'Advisory'}</p></div>
      ))}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">Sprint {status.sprint_id}</p>
          <p className={`text-sm ${muted}`}>{status.objectives} objectives · {status.excellence_modules} modules · {status.standard_categories} categories</p>
          <p className={`text-sm ${accent}`}>Code quality · Architecture · Reliability · Release engineering</p>
        </div>
      )}
    </div>
  );
}
